import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ConnectivityBadge } from "../../components/devices/ConnectivityBadge";
import { LIVE_REFRESH_INTERVAL_MS, useLiveNow } from "../../lib/useLiveNow";
import { invalidatePolicyViews } from "../../lib/policyQueries";
import { AppIcon } from "../../components/ui/AppIcon";
import { BulkActionBar } from "../../components/ui/BulkActionBar";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { MasterDetailLayout } from "../../components/ui/MasterDetailLayout";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchInput } from "../../components/ui/SearchInput";
import { SegmentedFilter } from "../../components/ui/SegmentedFilter";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import {
  type AdminDeviceStatusTarget,
  type DeviceLifecycleStatus,
  type DeviceListRow,
  type DeviceStatusFilter,
  type Platform,
  devicesApi,
} from "../../lib/api/devices";
import {
  LIFECYCLE_STATUS_OPTIONS,
  lifecycleStatusLabel,
  lifecycleTone,
} from "../../lib/deviceLifecycle";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";
import { DeviceDetailPanel } from "./DeviceDetailPanel";

type StatusValue = "all" | DeviceStatusFilter;

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function DevicesListPage() {
  const [locale] = useLocale();
  const now = useLiveNow();
  const queryClient = useQueryClient();
  const { push: pushToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [platform, setPlatform] = useState<Platform | "">("");
  const [status, setStatus] = useState<StatusValue>("all");
  const [lifecycleStatus, setLifecycleStatus] = useState<DeviceLifecycleStatus | "">("");
  const [search, setSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [confirmBulkDeprovision, setConfirmBulkDeprovision] = useState(false);

  const selectedId = searchParams.get("id");

  const query = useQuery({
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
    queryKey: ["devices", "list", platform, status, lifecycleStatus, search],
    queryFn: () =>
      devicesApi.list({
        platform: platform || undefined,
        status: status === "all" ? undefined : status,
        lifecycle_status: lifecycleStatus || undefined,
        search: search || undefined,
      }),
  });

  const invalidateAll = () => {
    void invalidatePolicyViews(queryClient);
  };

  const bulkForceRefresh = useMutation({
    mutationFn: (ids: string[]) => devicesApi.bulkForceRefresh(ids),
    onSuccess: (data) => {
      invalidateAll();
      setSelectedKeys(new Set());
      pushToast({
        tone: data.failed.length > 0 ? "warning" : "success",
        title: formatMessage("devices.bulk.forceRefresh.toast", {
          succeeded: String(data.succeeded.length),
          failed: String(data.failed.length),
        }),
      });
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  const bulkSetStatus = useMutation({
    mutationFn: ({ ids, target }: { ids: string[]; target: AdminDeviceStatusTarget }) =>
      devicesApi.bulkSetStatus(ids, target),
    onSuccess: (data) => {
      invalidateAll();
      setSelectedKeys(new Set());
      pushToast({
        tone: data.failed.length > 0 ? "warning" : "success",
        title: formatMessage("devices.bulk.setStatus.toast", {
          target: t(`devices.lifecycleStatus.${data.target_status}`, data.target_status),
          succeeded: String(data.succeeded.length),
          skipped: String(data.skipped.length),
          failed: String(data.failed.length),
        }),
      });
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  const openDetail = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    setSearchParams(next, { replace: false });
  };

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: false });
  };

  const handleBulkStatus = (target: AdminDeviceStatusTarget) => {
    const ids = Array.from(selectedKeys);
    if (ids.length === 0) return;
    if (target === "deprovisioned") {
      setConfirmBulkDeprovision(true);
      return;
    }
    bulkSetStatus.mutate({ ids, target });
  };

  const handleBulkForceRefresh = () => {
    const ids = Array.from(selectedKeys);
    if (ids.length === 0) return;
    bulkForceRefresh.mutate(ids);
  };

  const columns: Column<DeviceListRow>[] = useMemo(
    () => [
      {
        key: "user",
        header: t("devices.list.column.user"),
        render: (row) => {
          const displayName =
            row.enrollee?.full_name ||
            row.user?.full_name ||
            row.user?.email ||
            row.model ||
            row.device_uid;
          return (
            <div className="app-cell">
              <AppIcon name={displayName} packageId={row.device_uid} size={32} />
              <div>
                <button
                  type="button"
                  className="app-name app-name--link"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetail(row.id);
                  }}
                  style={{
                    background: "none",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                    color: "inherit",
                    font: "inherit",
                    textAlign: "left",
                  }}
                >
                  {displayName}
                </button>
                <div className="app-pkg">{row.device_uid}</div>
              </div>
            </div>
          );
        },
      },
      {
        key: "platform",
        header: t("devices.list.column.platform"),
        render: (row) => (
          <span className="badge badge--info">{t(`common.platform.${row.platform}`)}</span>
        ),
      },
      {
        key: "model",
        header: t("devices.list.column.model"),
        render: (row) => row.model || "—",
      },
      {
        key: "os",
        header: t("devices.list.column.osVersion"),
        render: (row) => <span className="mono">{row.os_version || "—"}</span>,
      },
      {
        key: "last_seen",
        header: t("devices.list.column.lastSeen"),
        width: "11rem",
        render: (row) => (
          <span className="mono table-cell--datetime">{formatDateTime(row.last_seen_at)}</span>
        ),
      },
      {
        key: "status",
        header: t("operations.connectivity.title"),
        render: (row) => <ConnectivityBadge lastSeenAt={row.last_seen_at} isOnline={row.is_online} now={now} />,
      },
      {
        key: "policy_enabled",
        header: t("operations.policy.listLabel"),
        render: (row) => (
          <span className={row.policy_enabled === false ? "badge badge--warning" : "badge"}>
            {t(typeof row.policy_enabled !== "boolean" ? "operations.connectivity.unknown"
              : row.policy_enabled ? "operations.policy.enabled" : "operations.policy.disabled")}
          </span>
        ),
      },
      {
        key: "last_policy_fetched_at",
        header: t("operations.policy.lastFetch"),
        render: (row) => <span className="mono table-cell--datetime">{formatDateTime(row.last_policy_fetched_at)}</span>,
      },
      {
        key: "lifecycle",
        header: t("devices.list.column.lifecycle"),
        render: (row) => {
          const tone = lifecycleTone(row.status);
          const map: Record<string, string> = {
            success: "badge--success",
            info: "badge--info",
            warning: "badge--warning",
            danger: "badge--danger",
            neutral: "",
          };
          return (
            <span className={`badge ${map[tone] ?? ""}`}>{lifecycleStatusLabel(row.status)}</span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, locale, now],
  );

  const bulkBusy = bulkForceRefresh.isPending || bulkSetStatus.isPending;

  const listContent = (
    <div>
      <PageHeader title={t("devices.list.title")} subtitle={t("devices.list.subtitle")} />

      <div className="filter-bar">
        <SearchInput
          placeholder={t("devices.filter.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <SegmentedFilter<StatusValue>
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: t("common.all") },
            { value: "online", label: t("devices.statusValue.online"), dot: "allow" },
            { value: "recent", label: t("devices.statusValue.recent") },
            { value: "stale", label: t("devices.statusValue.stale"), dot: "unknown" },
          ]}
        />
        <Select
          value={platform}
          onChange={(e) => setPlatform((e.target.value as Platform) || "")}
          aria-label={t("devices.filter.platform")}
        >
          <option value="">{t("devices.filter.platform")}</option>
          <option value="android">{t("common.platform.android")}</option>
          <option value="ios">{t("common.platform.ios")}</option>
        </Select>
        <Select
          value={lifecycleStatus}
          onChange={(e) => setLifecycleStatus((e.target.value as DeviceLifecycleStatus) || "")}
          aria-label={t("devices.filter.lifecycle")}
        >
          <option value="">{t("devices.filter.lifecycle")}</option>
          {LIFECYCLE_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {lifecycleStatusLabel(s)}
            </option>
          ))}
        </Select>
      </div>

      <p className="muted">{t("operations.connectivity.refreshHint")}</p>
      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button
          size="sm"
          onClick={handleBulkForceRefresh}
          loading={bulkForceRefresh.isPending}
          disabled={bulkBusy}
        >
          {t("devices.bulk.action.forceRefresh")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleBulkStatus("active")}
          loading={bulkSetStatus.isPending && bulkSetStatus.variables?.target === "active"}
          disabled={bulkBusy}
        >
          {t("devices.bulk.action.activate")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleBulkStatus("disabled")}
          loading={bulkSetStatus.isPending && bulkSetStatus.variables?.target === "disabled"}
          disabled={bulkBusy}
        >
          {t("devices.bulk.action.disable")}
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => handleBulkStatus("deprovisioned")}
          loading={
            bulkSetStatus.isPending && bulkSetStatus.variables?.target === "deprovisioned"
          }
          disabled={bulkBusy}
        >
          {t("devices.bulk.action.deprovision")}
        </Button>
      </BulkActionBar>

      <Card padding="none">
        {query.isLoading && <LoadingState />}
        {query.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(query.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        {query.data && (
          <Table
            rows={query.data}
            columns={columns}
            rowKey={(row) => row.id}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            onRowClick={(row) => openDetail(row.id)}
            rowClassName="table-row--clickable"
            empty={
              <EmptyState titleKey="devices.list.emptyTitle" bodyKey="devices.list.emptyBody" />
            }
          />
        )}
      </Card>

      <ConfirmDialog
        open={confirmBulkDeprovision}
        title={formatMessage("devices.bulk.deprovision.confirmTitle", {
          count: String(selectedKeys.size),
        })}
        body={t("devices.bulk.deprovision.confirm")}
        confirmLabel={t("devices.bulk.action.deprovision")}
        tone="danger"
        loading={bulkSetStatus.isPending && bulkSetStatus.variables?.target === "deprovisioned"}
        onCancel={() => setConfirmBulkDeprovision(false)}
        onConfirm={() => {
          bulkSetStatus.mutate(
            { ids: Array.from(selectedKeys), target: "deprovisioned" },
            { onSuccess: () => setConfirmBulkDeprovision(false) },
          );
        }}
      />
    </div>
  );

  return (
    <MasterDetailLayout
      list={listContent}
      detail={
        selectedId ? <DeviceDetailPanel key={selectedId} deviceId={selectedId} onClose={closeDetail} /> : null
      }
      onClose={closeDetail}
      ariaLabel={t("devices.detail.title")}
    />
  );
}
